import { useState } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import { useCfmContext } from "../../Context/CfmContext";
import useDraggableDialog from "./Utils/UseDraggableDialog";
import InputTextArea from "../Inputs/InputTextArea";
import { useClientMeta } from "../../Hooks/UseClientMeta";
import AddButton from "../Buttons/AddButton";

/**
 * NotesDialogBox component renders a draggable dialog box for adding or editing notes
 * associated with a selected person and active tab. It allows users to input notes,
 * and save them to the application's context state. The dialog is only editable if
 * the user is the owner of the client.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClose - Callback function to close the dialog box
 * @param {string} props.selectedPerson - The identifier or name of the selected person
 * @param {string} props.activeTab - The currently active tab for which notes are being edited
 *
 * @returns {JSX.Element} The rendered NotesDialogBox component
 */
const NotesDialogBox = ({ onClose, selectedPerson, activeTab }) => {
  const { state, dispatch } = useCfmContext();
  const { ref, position, handleMouseDown } = useDraggableDialog();
  const initialValue = state.graphNotes?.[selectedPerson]?.[activeTab] || "";
  const [noteText, setNoteText] = useState(initialValue);
  const { isOwnClient } = useClientMeta();

  /**
   * Dispatches an action to update the graph notes for a specific person and tab.
   *
   * @function
   * @returns {void}
   *
   * @description
   * Gathers the currently selected person, active tab, and note text,
   * then dispatches an action of type "UPDATE_GRAPH_NOTES" with these values as the payload.
   * Intended to be called when the user saves notes in the dialog box.
   */
  const handleSave = () => {
    dispatch({
      type: "UPDATE_GRAPH_NOTES",
      payload: {
        person: selectedPerson,
        tab: activeTab,
        value: noteText,
      },
    });
  };

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Notes</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      <div className="personaldetails-dialogbox-inputrow">
        <div className="personaldetails-dialogbox-inputColumn">
          {/* <div className="Title"></div> */}
          <div className="textarea-container">
            <InputTextArea
              label={`${selectedPerson} - ${activeTab}`}
              value={noteText}
              onChange={(value) => setNoteText(value)}
              isDisabled={state.details?.length === 0}
              placeholder="Enter your notes here"
              type="text"
              charLimit={150} // Optional: max characters allowed
            />
          </div>
          {isOwnClient && (
            <div className="notes-button-container">
              <AddButton
                buttonText="Save"
                onClick={() => {
                  handleSave();
                  onClose();
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotesDialogBox;
